import { cn } from "../../lib/utils";
import { homeMealBbsTone } from "../../utils/homeBlueTheme";

type MealBbsScoreRowProps = {
  score: number;
  label?: string;
  provisional?: boolean;
  className?: string;
};

const MealBbsScoreRow = ({
  score,
  label,
  provisional,
  className,
}: MealBbsScoreRowProps) => {
  const tone = homeMealBbsTone(label);
  const radius = 26;
  const stroke = 4;
  const normalizedRadius = radius - stroke / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const progress = Math.min((score / 100) * 100, 100);
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border px-3 py-3",
        tone.shell,
        className,
      )}
    >
      <div className="relative flex h-14 w-14 shrink-0 items-center justify-center">
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${radius * 2} ${radius * 2}`}
          className="rotate-[-90deg]"
          aria-hidden
        >
          <circle
            className={tone.track}
            strokeWidth={stroke}
            fill="transparent"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          <circle
            className={tone.stroke}
            strokeWidth={stroke}
            strokeDasharray={`${circumference} ${circumference}`}
            style={{ strokeDashoffset }}
            strokeLinecap="round"
            fill="transparent"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
        </svg>
        <span
          className={cn(
            "absolute text-lg font-black tabular-nums leading-none",
            tone.score,
          )}
        >
          {score}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-foreground">
          Meal BBS
        </p>
        <p className="mt-0.5 text-compact-sm text-muted-foreground">
          This meal score
          {provisional ? " · Provisional" : ""}
        </p>
      </div>

      {label ? (
        <span
          className={cn(
            "shrink-0 rounded-full px-3 py-1 text-compact-xs font-bold uppercase tracking-wide",
            tone.badge,
          )}
        >
          {label}
        </span>
      ) : null}
    </div>
  );
};

export default MealBbsScoreRow;
